'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/app/homepage/components/Footer';

export default function AboutPage() {
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const coreValues = [
  {
    id: 'value_1',
    icon: 'ShieldCheckIcon',
    title: 'Quality First',
    description: 'Strict European manufacturing standards.'
  },
  {
    id: 'value_2',
    icon: 'HeartIcon',
    title: 'Farmer Focused',
    description: 'We grow when farmers grow.'
  },
  {
    id: 'value_3',
    icon: 'LightBulbIcon',
    title: 'Innovation',
    description: 'Research-driven formulations backed by science.'
  },
  {
    id: 'value_4',
    icon: 'CheckBadgeIcon',
    title: 'Integrity',
    description: 'Transparent and reliable partnerships.'
  }];


  const whyUgandaPoints = [
  {
    id: 'uganda_1',
    icon: 'ChartBarIcon',
    title: 'Rising Demand',
    description: 'Growing population and urbanization driving increased protein consumption.'
  },
  {
    id: 'uganda_2',
    icon: 'AcademicCapIcon',
    title: 'Farmer Development',
    description: 'Opportunity to support small and medium-scale farmers with quality inputs.'
  }];


  const commitmentStats = [
  {
    id: 'stat_1',
    value: '100%',
    label: 'Dutch Formulated',
    icon: 'BeakerIcon'
  },
  {
    id: 'stat_2',
    value: '24/7',
    label: 'Quality Monitoring',
    icon: 'ClockIcon'
  },
  {
    id: 'stat_3',
    value: '500+',
    label: 'Growing Farmer Network',
    icon: 'UserGroupIcon'
  }];


  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="relative w-full h-full bg-gradient-to-br from-green-900 to-green-700">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?w=1920')] bg-cover bg-center opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-green-900/90 via-green-800/80 to-green-900/90" />
          </div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto text-center pt-32 pb-20 px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 mb-8 reveal">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-sm font-semibold text-white">About Bloom Animal Feeds</span>
          </div>

          <h1 className="text-heading-1 md:text-[3.75rem] lg:text-[4.5rem] leading-[1.1] text-white mb-6 reveal">
            Bridging European Excellence with Local Expertise
          </h1>

          <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto reveal" style={{ animationDelay: '0.1s' }}>
            Bloom Animal Feeds Limited is a Netherlands-based poultry nutrition brand bringing advanced European feed technology to Uganda's rapidly growing poultry sector.
          </p>

          <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto mt-4 reveal" style={{ animationDelay: '0.2s' }}>
            We specialize in scientifically formulated feed concentrates designed to maximize poultry health, productivity, and farm profitability.
          </p>

          <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto mt-4 reveal" style={{ animationDelay: '0.3s' }}>
            Our products are manufactured in Holland under strict European quality control systems and distributed across Uganda through trusted agro-vet partners.
          </p>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto mt-12 reveal" style={{ animationDelay: '0.4s' }}>
            <div className="aspect-[16/9] bg-gradient-to-br from-green-100 to-green-50">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1c8c0bf81-1764687936488.png"
                alt="Modern poultry farm in Uganda with organized feeding systems and healthy chickens"
                className="w-full h-full object-cover" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-section px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Vision */}
            <div className="reveal">
              <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 mb-6">
                <Icon name="EyeIcon" size={20} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Our Vision</span>
              </div>
              <h2 className="text-heading-2 md:text-[2.75rem] leading-tight text-foreground mb-6">
                To become East Africa's most trusted poultry nutrition partner.
              </h2>
            </div>

            {/* Mission */}
            <div className="reveal" style={{ animationDelay: '0.2s' }}>
              <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 mb-6">
                <Icon name="RocketLaunchIcon" size={20} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Our Mission</span>
              </div>
              <h2 className="text-heading-2 md:text-[2.75rem] leading-tight text-foreground mb-6">
                To empower poultry farmers with high-performance feed solutions that improve productivity, efficiency, and profitability.
              </h2>
            </div>
          </div>

          {/* Featured Images Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            <div className="relative rounded-2xl overflow-hidden shadow-xl reveal">
              <div className="aspect-[4/3] bg-gradient-to-br from-green-100 to-green-50">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1c5f74af9-1768060556989.png"
                  alt="White broiler chickens at different growth stages in modern poultry house"
                  className="w-full h-full object-cover" />
                
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl reveal" style={{ animationDelay: '0.1s' }}>
              <div className="aspect-[4/3] bg-gradient-to-br from-green-100 to-green-50">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1c3eaa708-1766476491687.png"
                  alt="Farmer inspecting feed quality and checking poultry health in farm setting"
                  className="w-full h-full object-cover" />
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Our Core Values</p>
            <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground">
              The principles that guide <br />
              <span className="gradient-text">everything we do</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coreValues.map((value, index) =>
            <div
              key={value.id}
              className="glass-card rounded-2xl p-8 hover:shadow-xl transition-all duration-300 reveal"
              style={{ animationDelay: `${index * 0.1}s` }}>
              
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name={value.icon as any} size={28} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-heading-3 text-foreground mb-2">{value.title}</h3>
                    <p className="text-base text-muted-foreground">{value.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Uganda Section */}
      <section className="py-section px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Why Uganda?</p>
            <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
              A Rapidly Growing Market
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Uganda's poultry industry is among the fastest-growing livestock sectors in East Africa. With increasing demand for eggs and poultry meat, farmers need consistent, high-quality nutrition solutions to compete effectively.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mt-4">
              Bloom Animal Feeds delivers the same quality standards used by leading poultry operations in Europe — adapted for Uganda's local conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyUgandaPoints.map((point, index) =>
            <div
              key={point.id}
              className="glass-card rounded-2xl p-8 hover:shadow-xl transition-all duration-300 reveal"
              style={{ animationDelay: `${index * 0.1}s` }}>
              
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon name={point.icon as any} size={32} className="text-primary" />
                  </div>
                  <h3 className="text-heading-3 text-foreground mb-3">{point.title}</h3>
                  <p className="text-base text-muted-foreground">{point.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Commitment to Excellence Section */}
      <section className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Our Commitment to Excellence</p>
            <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
              Every batch of Bloom feed concentrate represents our dedication to quality and farmer success.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {commitmentStats.map((stat, index) =>
            <div key={stat.id} className="text-center reveal" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
                  <Icon name={stat.icon as any} size={32} className="text-primary" />
                </div>
                <div className="text-5xl md:text-6xl font-bold text-foreground mb-2">{stat.value}</div>
                <div className="text-base text-muted-foreground">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>);

}